import { z } from 'zod'

// Sources métier disponibles pour un champ de gabarit (doit rester aligné avec l'enum backend).
export const SIGNABLE_FIELD_SOURCES = [
  'pharmacy_name',
  'pharmacy_cip',
  'pharmacy_siret',
  'pharmacy_address',
  'pharmacy_zipcode',
  'pharmacy_city',
  'pharmacy_tva',
  'pharmacy_capital',
  'pharmacy_rcs',
  'user_titulaire',
  'user_rpps',
  'user_civility',
  'user_lastname',
  'user_firstname',
  'program_name',
  'program_period',
  'program_date_start',
  'program_date_end',
  'program_survey_cost',
  'program_convention_subject',
  'program_convention_forme',
  'program_convention_objectifs',
  'prestations_table',
  'store_bag_date_start',
  'store_bag_date_end',
  'sound_spots_table',
  'sound_date_start',
  'sound_date_end',
  'partner_cadre_signed_date',
  'partner_cluster',
  'partner_campaign_date_start',
  'partner_campaign_date_end',
  'prestations_total_ht',
  'prestations_total_ttc',
  'current_date',
  'free_text',
  'merge_text',
  'signature_anchor',
  // Champs interactifs Yousign, cochés par le signataire pendant la signature : case à cocher
  // et option d'un groupe radio (choix unique). Positionnés par coordonnées, jamais dessinés.
  'checkbox',
  'radio_option',
] as const

export type SignableFieldSource = (typeof SIGNABLE_FIELD_SOURCES)[number]

export const SIGNABLE_DOCUMENT_TYPE_DEFAULT = 'signable_document'
// Scope par défaut (rétro-compatibilité). Le champ entity est désormais éditable (module transverse).
export const SIGNABLE_TEMPLATE_ENTITY = 'insight'

// Scopes disponibles pour le champ entity (doit rester aligné avec User::ENTITY_* côté backend).
// labelKey -> components.insight.signableDocumentTemplate.entities.<labelKey>
export const SIGNABLE_TEMPLATE_ENTITY_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'insight', labelKey: 'insight' },
  { value: 'sound', labelKey: 'sound' },
  { value: 'store', labelKey: 'store' },
  { value: 'store_bag', labelKey: 'storeBag' },
  { value: 'school', labelKey: 'school' },
  { value: 'group', labelKey: 'group' },
  { value: 'call', labelKey: 'call' },
]

// Champ d'un gabarit (avec coordonnées overlay en millimètres).
export const signableFieldSchema = z.object({
  id: z.number().optional(),
  placeholder: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_]+$/, 'snake_case only (^[a-z0-9_]+$)'),
  label: z.string().min(1),
  source: z.enum(SIGNABLE_FIELD_SOURCES),
  defaultValue: z.string().nullable().optional(),
  editableAtProgramSetup: z.boolean().default(false),
  position: z.number().int().min(0).default(0),
  // Coordonnées overlay
  page: z.number().int().min(1).default(1),
  posX: z.number(),
  posY: z.number(),
  fontSize: z.number().int().min(4).max(72).default(10),
  maxWidth: z.number().nullable().optional(),
  multiline: z.boolean().default(false),
  // Champs interactifs Yousign : case / groupe radio FACULTATIF pour le signataire (obligatoire
  // par défaut). Sans effet sur les autres sources.
  optional: z.boolean().default(false),
  // Option radio : nom du groupe Yousign — les options partageant ce nom forment un seul choix.
  groupName: z.string().max(100).nullable().optional(),
})

export type SignableFieldInput = z.infer<typeof signableFieldSchema>

/**
 * Champ tel que servi par l'API : porte en plus la TRACE de modification, en lecture seule.
 * Ces clés ne sont volontairement pas dans `signableFieldSchema` — Zod les retire donc du
 * payload à l'enregistrement, le serveur restant seul à les écrire.
 */
export type SignableFieldRow = Omit<SignableFieldInput, 'optional'> & {
  /** Facultatif pour le signataire (case / radio) ; absent sur les champs servis avant l'ajout de la colonne. */
  optional?: boolean
  /** Date ISO de la dernière modification effective du champ (null si jamais tracée). */
  updatedAt?: string | null
  /** Auteur de cette modification (nom lisible calculé côté API). */
  updatedByName?: string | null
}

// Création / mise à jour d'un gabarit. `entity` est figé à 'insight' pour ce module.
export const signableDocumentTemplateUpsertSchema = z.object({
  label: z.string().min(1),
  entity: z.string().default(SIGNABLE_TEMPLATE_ENTITY),
  // Labo partenaire proprietaire (IRI) : alimente = modele propose uniquement pour ce labo.
  partner: z.string().regex(/^\/api\/v2\/companies\/\d+$/).nullable().optional(),
  sourcePdfPath: z.string().nullable().optional(),
  documentType: z.string().default(SIGNABLE_DOCUMENT_TYPE_DEFAULT),
  active: z.boolean().default(true),
  fields: z.array(signableFieldSchema).default([]),
})

export type SignableDocumentTemplateUpsertInput = z.infer<
  typeof signableDocumentTemplateUpsertSchema
>

// Prévisualisation : mode fictif (jeu de données factice) ou via un CIP réel.
export const signablePreviewSchema = z
  .object({
    mode: z.enum(['fixture', 'cip']),
    cip: z.string().optional(),
  })
  .refine(data => data.mode !== 'cip' || (!!data.cip && data.cip.trim().length > 0), {
    message: 'CIP required when mode is "cip"',
    path: ['cip'],
  })

export type SignablePreviewInput = z.infer<typeof signablePreviewSchema>

export type PreviewMode = SignablePreviewInput['mode']

// Liaison programme <-> template au paramétrage : template choisi + valeurs des champs editableAtProgramSetup.
export const conventionBindingSchema = z.object({
  template: z.union([z.string(), z.number()]),
  values: z.record(z.string(), z.string()).default({}),
})

export type ConventionBindingInput = z.infer<typeof conventionBindingSchema>

// Déclaration de transparence (dispositif EPS) : marquage « envoyée » / « non envoyée » d'une
// instance de document signable (PATCH /signable_document_instances/:id/send_at).
export const signableInstanceSendAtSchema = z.object({
  id: z.union([z.string(), z.number()]),
  checked: z.boolean(),
})

export type SignableInstanceSendAtInput = z.infer<typeof signableInstanceSendAtSchema>
